/**
 * Value axis and number formatting shared by the kit's charts: round tick
 * values (1 / 2 / 2.5 / 5 × 10ⁿ), labels that stay distinct and true to their
 * tick, and the compact notation for tight spots („3,23 Mio.“).
 */

/** 1/2/2.5/5 × 10^n — the step family for round axis ticks. Do not extend. */
const NICE_FRACS = [1, 2, 2.5, 5, 10] as const;

/** Default interval count: 5 tick values including 0 (or the negative min). */
export const NX_AXIS_INTERVALS = 4;

export interface NxValueAxis {
  min: number;
  max: number;
  /** Ascending tick values from min to max. */
  ticks: number[];
  /** Top-to-bottom (max → min). `fraction` is 0 at the plot top (max), 1 at the bottom (min). */
  yTicks: { value: number; fraction: number }[];
  /** Derived from the tick values — same order as `yTicks`. */
  gridFractions: number[];
  /** No data to scale: the axis is the placeholder 0–1. */
  degenerate: boolean;
}

function roundTick(value: number): number {
  if (value === 0) return 0;
  return Number(value.toPrecision(12));
}

/** Smallest 1/2/2.5/5 × 10^n value that is >= `value`. */
function niceCeil(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const exp = Math.floor(Math.log10(value));
  const pow = 10 ** exp;
  const frac = value / pow;
  for (const nf of NICE_FRACS) {
    if (frac <= nf + 1e-12) return nf * pow;
  }
  return 10 * pow;
}

function finishAxis(min: number, max: number, ticks: number[], degenerate: boolean): NxValueAxis {
  const span = max - min;
  const yTicks = [...ticks].reverse().map((value) => ({
    value,
    fraction: span === 0 ? 1 : (max - value) / span,
  }));
  return {
    min,
    max,
    ticks,
    yTicks,
    gridFractions: yTicks.map((t) => t.fraction),
    degenerate,
  };
}

function degenerateAxis(): NxValueAxis {
  return finishAxis(0, 1, [0, 1], true);
}

function positiveAxis(maxValue: number, n: number): NxValueAxis {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return degenerateAxis();
  let step = niceCeil(maxValue / n);
  let max = roundTick(step * n);
  // niceCeil's 1e-12 slack must not produce a max below the data.
  let guard = 0;
  while (max < maxValue && Number.isFinite(max) && guard < 8) {
    step = niceCeil(step * 1.0000000001);
    max = roundTick(step * n);
    guard += 1;
  }
  if (!Number.isFinite(max) || max <= 0) return degenerateAxis();
  const ticks = Array.from({ length: n + 1 }, (_, i) => roundTick(step * i));
  ticks[0] = 0;
  ticks[n] = max;
  return finishAxis(0, max, ticks, false);
}

/**
 * Nice axis over [min(0, minValue), max(0, maxValue)] — zero is always on it.
 * Default 4 intervals (5 ticks including 0, or including the negative min).
 */
export function valueAxis(
  minValue: number,
  maxValue: number,
  intervals = NX_AXIS_INTERVALS
): NxValueAxis {
  const n =
    Number.isFinite(intervals) && intervals >= 1 ? Math.floor(intervals) : NX_AXIS_INTERVALS;
  let lo = Number.isFinite(minValue) ? minValue : 0;
  let hi = Number.isFinite(maxValue) ? maxValue : 0;
  if (lo > hi) [lo, hi] = [hi, lo];
  lo = Math.min(0, lo);
  hi = Math.max(0, hi);

  if (lo === 0 && hi === 0) return degenerateAxis();
  if (lo === 0) return positiveAxis(hi, n);
  if (hi === 0) {
    const pos = positiveAxis(-lo, n);
    if (pos.degenerate) return degenerateAxis();
    const min = -pos.max;
    const ticks = pos.ticks.map((t) => roundTick(-t)).reverse();
    ticks[0] = min;
    ticks[ticks.length - 1] = 0;
    return finishAxis(min, 0, ticks, false);
  }

  const step = niceCeil((hi - lo) / n);
  if (!Number.isFinite(step) || step <= 0) return degenerateAxis();
  let min = roundTick(Math.floor(lo / step) * step);
  let max = roundTick(Math.ceil(hi / step) * step);
  if (min > lo) min = roundTick(min - step);
  if (max < hi) max = roundTick(max + step);
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return degenerateAxis();
  const nSteps = Math.max(1, Math.round((max - min) / step));
  const ticks: number[] = [];
  for (let i = 0; i <= nSteps; i++) {
    const v = roundTick(min + step * i);
    // Snap float residue (e.g. -0.6 + 3*0.2 = 1.11e-16) to an exact 0 so a
    // straddling domain never grows a SECOND zero via the fallback below.
    ticks.push(Math.abs(v) < step * 1e-9 ? 0 : v);
  }
  ticks[0] = min;
  ticks[ticks.length - 1] = max;
  if (!ticks.some((t) => t === 0)) {
    ticks.push(0);
    ticks.sort((a, b) => a - b);
  }
  return finishAxis(min, max, ticks, false);
}

/**
 * 0-based nice axis for a non-negative max. `count` is the number of intervals
 * (default 4 → 5 ticks including 0). max ≤ 0 or non-finite → the degenerate
 * axis { min: 0, max: 1, ticks: [0, 1] }.
 */
export function niceTicks(maxValue: number, count = NX_AXIS_INTERVALS): NxValueAxis {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return degenerateAxis();
  return valueAxis(0, maxValue, count);
}

/**
 * Compact numeral: the full grouped number below 100 000, compact notation
 * above („3,23 Mio.“). The exact value belongs in a tooltip.
 */
export function formatCompactNumber(value: number, locale?: string): string {
  if (!Number.isFinite(value)) return String(value);
  if (Math.abs(value) < 100_000) {
    return value.toLocaleString(locale, { maximumFractionDigits: 2 });
  }
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
}

function tickStep(ticks: readonly number[]): number {
  if (ticks.length < 2) return 1;
  let step = Infinity;
  for (let i = 1; i < ticks.length; i++) {
    const d = Math.abs(ticks[i]! - ticks[i - 1]!);
    if (d > 0 && d < step) step = d;
  }
  return Number.isFinite(step) ? step : 1;
}

function hasAdjacentDup(labels: string[]): boolean {
  for (let i = 1; i < labels.length; i++) {
    if (labels[i] === labels[i - 1]) return true;
  }
  return false;
}

/**
 * Format a tick so adjacent labels stay distinct. Step ≥ 1 (or |value| ≥ 100k):
 * compact. Step < 1: as many fraction digits as it takes for every label to
 * round-trip to its own tick.
 */
export function formatTickLabel(value: number, ticks: readonly number[], locale?: string): string {
  if (!Number.isFinite(value)) return String(value);
  const step = tickStep(ticks);
  if (!(step > 0) || step >= 1 || Math.abs(value) >= 100_000) {
    return formatCompactNumber(value, locale);
  }
  const fmt = (v: number, d: number) =>
    v.toLocaleString(locale, { maximumFractionDigits: d, minimumFractionDigits: 0 });
  // A label must ROUND-TRIP to its own tick value, not merely differ from its
  // neighbours: step 0.25 needs 2 digits, but distinctness alone was satisfied
  // by "0 / 0,3 / 0,5 / 0,8 / 1" — an axis whose gridline labels lie by 20%.
  const faithful = (d: number) => ticks.every((t) => Number(t.toFixed(d)) === t);
  let digits = Math.max(0, Math.ceil(-Math.log10(step) - 1e-12));
  let labels = ticks.map((t) => fmt(t, digits));
  while (digits < 12 && (!faithful(digits) || hasAdjacentDup(labels))) {
    digits += 1;
    labels = ticks.map((t) => fmt(t, digits));
  }
  const idx = ticks.findIndex((t) => t === value);
  if (idx >= 0) return labels[idx]!;
  return fmt(value, digits);
}
